import type { ErrorCodeType } from '@hunk/shared/apiDataErrorCode';

export interface GenericRes {
  errorCode?: ErrorCodeType;
  errorText?: string;
}

export class FetchApiError extends Error {
  constructor(
    readonly statusCode: number,
    readonly responseError: GenericRes
  ) {
    super(responseError.errorText ?? responseError.errorCode ?? `HTTP ${statusCode}`);
    this.name = 'FetchApiError';
  }
}

export interface FetchApiOptions<TReq> {
  apiPath: string;
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  data?: TReq;
  headers?: HeadersInit;
}

export interface FetchApiResult<TRes> {
  response: TRes;
  headers: Headers;
}

/** JSON call to the server. GET sends `data` as query string, other methods as JSON body. Throws FetchApiError on non-2xx. */
export async function fetchApi<TReq extends object, TRes extends GenericRes>({ apiPath, method = 'GET', data, headers }: FetchApiOptions<TReq>): Promise<FetchApiResult<TRes>> {
  let url = apiPath;
  const init: RequestInit = { method, headers: { ...(headers ?? {}) } };
  if (method === 'GET' && data && Object.keys(data).length > 0) {
    const q = new URLSearchParams(Object.entries(data).map(([k, v]) => [k, String(v)]));
    url += `${url.includes('?') ? '&' : '?'}${q.toString()}`;
  } else if (method !== 'GET') {
    init.headers = { 'content-type': 'application/json', ...(headers ?? {}) };
    init.body = JSON.stringify(data ?? {});
  }
  const res = await fetch(url, init);
  const body = (await res.json().catch(() => ({}))) as TRes;
  if (!res.ok) {
    throw new FetchApiError(res.status, body);
  }
  return { response: body, headers: res.headers };
}
